import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// What ngOptions selected when the model matched no option. It still reaches the server as
// gift[variant_id], where "?" casts to 0 while a blank casts to nil, so the string is load-bearing.
const UNKNOWN_OPTION = "?"

export default class extends Controller {
  static get targets() {
    return ["product", "variant", "variantGroup", "shiplessPanel", "addressPanel", "internationalForm"];
  }

  static get values() { return { variantsUrl: String, selectedVariantId: String }; }

  connect() {
    this.renderVariants([]);
    this.reload();
  }

  reload() {
    this.syncPanels();
    this.loadVariants();
  }

  syncPanels() {
    const product = this.selectedProduct();
    // Not a Values API candidate: this reads a data attribute off a <option> built by
    // GiftsHelper#gift_product_options, not off the controller's own root element (D13/P34).
    const shipless = product !== null && product.dataset.shipless === "true";

    this.togglePanel(this.shiplessPanelTarget, shipless);
    this.togglePanel(this.addressPanelTarget, !shipless);
  }

  loadVariants() {
    const product = this.selectedProduct();
    if (product === null) { return; }

    const url = new URL(this.variantsUrlValue, window.location.origin);
    url.searchParams.set("selected_product_id", product.value);

    const options = RequestHelper.defaultRequestOptions();
    // The action answers html or json off respond_to; $http asked for json, fetch asks for */*.
    options.headers = Object.assign({}, options.headers, { "Accept": "application/json" });

    fetch(url, options).
      then(response => {
        if (!response.ok) { throw new Error(`Variant lookup failed (${response.status})`); }
        return response.json();
      }).
      then(variants => this.renderVariants(variants)).
      catch(this.logFailedCall);
  }

  renderVariants(variants) {
    const sorted = variants.slice().sort((a, b) => this.compareNames(a.name, b.name));
    const selected = sorted.find(variant => String(variant.id) === this.selectedVariantIdValue);

    this.variantTarget.innerHTML = "";
    if (selected === undefined) { this.variantTarget.add(new Option("", UNKNOWN_OPTION, true, true)); }
    sorted.forEach(variant => {
      this.variantTarget.add(new Option(variant.name, variant.id, false, variant === selected));
    });

    // R4 skip: "hidden" is bootstrap-yeti.scss's app-wide utility class (D1), not a name this
    // controller owns.
    this.variantGroupTarget.classList.toggle("hidden", sorted.length === 0);
  }

  submitInternational(event) {
    event.preventDefault();
    event.currentTarget.setAttribute("disabled", "disabled");
    // Plain form.submit(): #intl-shipping-form carries no lt-single-submit and nothing binds a
    // jQuery submit handler to it, so jQuery's .submit() shorthand and the native method behave
    // identically here (stimulus-hardening/patterns.md #37).
    this.internationalFormTarget.submit();
  }

  selectedProduct() {
    const option = this.productTarget.selectedOptions[0];
    if (option === undefined || option.value === UNKNOWN_OPTION) { return null; }

    return option;
  }

  // Hiding alone would start submitting the hidden panel's fields; disabling keeps them out of
  // the submission.
  togglePanel(panel, visible) {
    // R4 skip: "hidden" is bootstrap-yeti.scss's app-wide utility class (D1), not a name this
    // controller owns.
    panel.classList.toggle("hidden", !visible);
    panel.querySelectorAll("input, select, textarea").forEach(field => { field.disabled = !visible; });
  }

  // Case-insensitive, matching how this list has always been ordered.
  compareNames(left, right) {
    const a = String(left).toLowerCase();
    const b = String(right).toLowerCase();
    if (a === b) { return 0; }

    return a < b ? -1 : 1;
  }

  logFailedCall(response) {
    console.warn(response);
  }
}
